import { OverflowGrid } from "../components/ListOverflow.jsx";
// src/v2/radar/RadarSpinePage.jsx
//
// RADAR-PHASE-1-SPINE (P-39): Three-band Radar page powered by the focus_items engine.
// RADAR-PHASE-2-LIFECYCLE (P-40): Snooze, dismiss, per-type inline verbs,
//   Mine/All scope persistence, undo toast, Dismissed section.
// RADAR-V2: Type-aware disposition panel (per-type primary verbs), Admin radar
//   settings dial, signal re-arm watermark, escalate verb.
//
// Layout:
//   Header: "Radar" h1 + pulsing green dot
//   Stat chips: Overdue / Due soon / Decisions / Issues / Unassigned
//   Toolbar:    type chips, search, All/Mine toggle, sort
//   Bands:      Act now / This cycle / Keep watch
//   Row expand: "Why Radar ranked this" + type-specific action buttons
//   Dismissed:  collapsed section at page bottom with Restore buttons
//   Toast:      dark bottom-centre with 6 s timeout + Undo link
//   Admin panel: Radar settings (admin role only)

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { T } from "../../app/tokens.js";
import {
  getFocusItemsFeed,
  runFocusItemEmitters,
  resolveFocusItem,
  unresolveFocusItem,
  snoozeFocusItem,
  unsnoozeFocusItem,
  dismissFocusItem,
  restoreFocusItem,
  getDismissedFocusItems,
  getSnoozedFocusItems,
} from "../../data/focusItemsClient.js";
import { log } from "../../utils/logger.js";
import { matchesFoldedQuery } from "../../utils/textSearch.js";
import { showToast } from "../../components/toastService.jsx";
import { KpiTile } from "../../components/KpiTile.jsx";
import { KpiTileRow } from "../../features/plan/KpiTileRow.jsx";
import { SearchInput } from "../components/SearchInput.jsx";
import { SortControl } from "../components/SortControl.jsx";
import { CompactSwitch } from "../components/ViewControls.jsx";
import { ConsolidatedFilter } from "../components/ConsolidatedFilter.jsx";
import { useUiPrefs } from "../hooks/useUiPrefs.js";
import { UI_PREFS_RADAR_V1 } from "../../services/portfolioStatePersistence.js";
import { PageHeaderStack } from "../../components/PageHeaderStack.jsx";
import { useFeatureFlags } from "../../contexts/FeatureFlagContext.jsx";
import { RadarDecisionsPanel } from "./RadarDecisionsPanel.jsx";

const RADAR_DECISIONS_FLAG = "FEATURE_RADAR_DECISIONS_REGISTER";

// ─── Constants ────────────────────────────────────────────────────────────────

const TYPE_LABELS = {
  decision: "Decision",
  action:   "Action",
  issue:    "Issue",
  review:   "Review",
  signal:   "Signal",
  flag:     "Flag",
};

const ALL_TYPES = ["decision", "action", "issue", "review", "signal", "flag"];

const BANDS = [
  { key: "act_now",    label: "Act now",    hint: "overdue or due within 30 days", color: "var(--v2-red)",   dotColor: "var(--v2-red)"   },
  { key: "this_cycle", label: "This cycle", hint: "due within 90 days",            color: "var(--v2-amber)", dotColor: "var(--v2-amber)" },
  { key: "keep_watch", label: "Keep watch", hint: "no due date · monitoring",      color: T.mid,             dotColor: T.mid             },
];

const SCOPE_PREF_KEY = "radar_scope_pref";

const RADAR_SORT_FIELDS = [
  { value: "priority",    label: "Priority"    },
  { value: "due_soonest", label: "Due soonest" },
];

// ConsolidatedFilter dimensions for item type.
// Multi-select; empty selection (or all selected) means "show all types".
const RADAR_FILTER_DIMENSIONS = [
  {
    id: "type",
    label: "Type",
    options: ALL_TYPES.map((t) => ({ value: t, label: TYPE_LABELS[t] })),
  },
];

// Decision sub-choice outcomes
const DECISION_OUTCOMES = [
  { label: "Renew",     outcome: "Renew"     },
  { label: "Negotiate", outcome: "Negotiate" },
  { label: "Exit",      outcome: "Exit"      },
  { label: "Defer",     outcome: "Defer"     },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function TypePill({ type }) {
  const colors = {
    decision: { bg: "var(--v2-purple-tint)",  color: T.decisionFg         },
    action:   { bg: "var(--v2-green-tint)",   color: "var(--v2-green)"   },
    issue:    { bg: "var(--v2-red-tint)",     color: "var(--v2-red)"     },
    review:   { bg: "var(--v2-amber-tint)",   color: "var(--v2-amber)"   },
    signal:   { bg: "var(--v2-blue-tint)",    color: T.reviewLeaseChipFg  },
    flag:     { bg: "var(--v2-amber-tint)",   color: "var(--v2-amber)"   },
  };
  const c = colors[type] ?? { bg: "var(--v2-hair)", color: "var(--v2-mid)" };
  return (
    <span
      data-testid={`type-pill-${type}`}
      style={{
        background: c.bg, color: c.color,
        fontSize: T.fsXxxs, fontWeight: T.fwBold, letterSpacing: "0.03em",
        textTransform: "uppercase", padding: "3px 8px", borderRadius: 6,
        whiteSpace: "nowrap", width: 64, textAlign: "center", display: "inline-block",
        flexShrink: 0,
      }}
    >
      {TYPE_LABELS[type] ?? type}
    </span>
  );
}

function DueLabel({ dueAt }) {
  if (!dueAt) return <span style={{ width: 82, textAlign: "right", fontSize: T.fsSm5, color: "var(--v2-mid)", flexShrink: 0 }}>—</span>;
  const today = new Date(); today.setUTCHours(0, 0, 0, 0);
  const due   = new Date(dueAt); due.setUTCHours(0, 0, 0, 0);
  const days  = Math.round((due - today) / 86_400_000);

  let label, color, fw;
  if (days < 0) {
    label = `Overdue ${Math.abs(days)}d`;
    color = "var(--v2-red)"; fw = T.fwBold;
  } else if (days <= 14) {
    label = `Due ${days}d`;
    color = "var(--v2-amber)"; fw = T.fwBold;
  } else {
    const d = due.toISOString().slice(0, 10);
    label = d; color = "var(--v2-mid)"; fw = T.fwRegular;
  }
  return (
    <span style={{ width: 82, textAlign: "right", fontSize: T.fsSm5, color, fontWeight: fw, flexShrink: 0 }}>
      {label}
    </span>
  );
}

function UrgencyScore({ score }) {
  if (score == null) return <span style={{ width: 52, flexShrink: 0 }} />;
  const rounded = Math.round(score);
  let color = T.mid;
  if (rounded >= 60) color = "var(--v2-red)";
  else if (rounded >= 20) color = "var(--v2-amber)";
  return (
    <span
      data-testid="urgency-score"
      title="Urgency score"
      style={{
        width: 52, textAlign: "right", fontSize: T.fsSm5,
        fontWeight: T.fwBold, color, flexShrink: 0,
      }}
    >
      {rounded}
    </span>
  );
}

function AvatarOwner({ ownerId }) {
  if (!ownerId) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, width: 112, flexShrink: 0 }}>
        <span style={{ width: 22, height: 22, borderRadius: "50%", background: T.lightGray, color: "var(--v2-slate-bar)", fontSize: T.fsXxxs, fontWeight: T.fwBold, display: "grid", placeItems: "center", flexShrink: 0 }}>?</span>
        <span style={{ fontSize: T.fsSm5, color: "var(--v2-mid)", fontStyle: "italic" }}>Unassigned</span>
      </div>
    );
  }
  const initials = ownerId.split("@")[0].slice(0, 2).toUpperCase();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, width: 112, flexShrink: 0 }}>
      <span style={{ width: 22, height: 22, borderRadius: "50%", background: T.borderCompact, color: T.hubSlate, fontSize: T.fsXxxs, fontWeight: T.fwBold, display: "grid", placeItems: "center", flexShrink: 0 }}>{initials}</span>
      <span style={{ fontSize: T.fsSm5, color: "var(--v2-mid)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ownerId}</span>
    </div>
  );
}


// ─── Snooze Picker (inline) ───────────────────────────────────────────────────

function SnoozePicker({ onSnooze, onCancel }) {
  const [customDate, setCustomDate] = useState("");
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const nextWeek  = new Date(); nextWeek.setDate(nextWeek.getDate() + 7);
  const minDate   = tomorrow.toISOString().slice(0, 10);

  return (
    <div
      data-testid="snooze-picker"
      style={{
        background: "var(--v2-card)", border: "1px solid var(--v2-line)",
        borderRadius: 10, padding: "12px 14px", marginTop: 8,
        display: "flex", flexDirection: "column", gap: 8,
      }}
    >
      <div style={{ fontSize: T.fsXxxs, fontWeight: T.fwBold, color: "var(--v2-mid)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        Snooze until…
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <button
          data-testid="snooze-tomorrow"
          onClick={() => onSnooze(tomorrow.toISOString())}
          style={{ fontSize: T.fsSm, fontWeight: T.fwBold, padding: "6px 12px", borderRadius: 8, border: "1px solid var(--v2-line)", background: "var(--v2-card)", cursor: "pointer" }}
        >
          Tomorrow
        </button>
        <button
          data-testid="snooze-next-week"
          onClick={() => onSnooze(nextWeek.toISOString())}
          style={{ fontSize: T.fsSm, fontWeight: T.fwBold, padding: "6px 12px", borderRadius: 8, border: "1px solid var(--v2-line)", background: "var(--v2-card)", cursor: "pointer" }}
        >
          Next week
        </button>
        <input
          data-testid="snooze-date-input"
          type="date"
          min={minDate}
          value={customDate}
          onChange={(e) => setCustomDate(e.target.value)}
          style={{ fontSize: T.fsSm, padding: "6px 10px", borderRadius: 8, border: "1px solid var(--v2-line)", background: "var(--v2-card)" }}
        />
        {customDate && (
          <button
            data-testid="snooze-confirm-custom"
            onClick={() => onSnooze(new Date(customDate + "T12:00:00").toISOString())}
            style={{ fontSize: T.fsSm, fontWeight: T.fwBold, padding: "6px 12px", borderRadius: 8, border: `1px solid ${T.reviewLeaseChipFg}`, background: T.reviewLeaseChipFg, color: T.white, cursor: "pointer" }}
          >
            Confirm
          </button>
        )}
        <button
          data-testid="snooze-cancel"
          onClick={onCancel}
          style={{ fontSize: T.fsSm, padding: "6px 10px", borderRadius: 8, border: "1px solid var(--v2-line)", background: "transparent", color: "var(--v2-mid)", cursor: "pointer" }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Decision Log Form (inline, for free-text decisions) ─────────────────────

function DecisionForm({ item, onSubmit, onCancel }) {
  const [decisionText, setDecisionText] = useState("");
  const [ownerName,    setOwnerName]    = useState("");
  const [targetDate,   setTargetDate]   = useState("");

  const handleSubmit = () => {
    if (!decisionText.trim()) return;
    onSubmit({
      verb:       "log_decision",
      decision:   decisionText.trim(),
      ownerName:  ownerName.trim() || null,
      targetDate: targetDate       || null,
      entityType: item.subject_type ?? "lease",
      entityId:   item.subject_id   ?? item.id,
    });
  };

  return (
    <div
      data-testid="decision-form"
      style={{
        background: "var(--v2-card)", border: "1px solid var(--v2-line)",
        borderRadius: 10, padding: "14px 16px", marginTop: 10,
        display: "flex", flexDirection: "column", gap: 10,
      }}
    >
      <div style={{ fontSize: T.fsXxxs, fontWeight: T.fwBold, color: "var(--v2-mid)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        Log decision
      </div>
      <textarea
        data-testid="decision-text-input"
        placeholder="What was decided?"
        value={decisionText}
        onChange={(e) => setDecisionText(e.target.value)}
        rows={2}
        style={{
          width: "100%", resize: "vertical", fontSize: T.fsSm5,
          padding: "8px 10px", borderRadius: 8,
          border: "1px solid var(--v2-line)", background: "var(--v2-card)",
          boxSizing: "border-box",
        }}
      />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input
          data-testid="decision-owner-input"
          type="text"
          placeholder="Owner name (optional)"
          value={ownerName}
          onChange={(e) => setOwnerName(e.target.value)}
          style={{ fontSize: T.fsSm5, padding: "6px 10px", borderRadius: 8, border: "1px solid var(--v2-line)", flex: 1, minWidth: 160 }}
        />
        <input
          data-testid="decision-date-input"
          type="date"
          value={targetDate}
          onChange={(e) => setTargetDate(e.target.value)}
          style={{ fontSize: T.fsSm5, padding: "6px 10px", borderRadius: 8, border: "1px solid var(--v2-line)" }}
        />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          data-testid="decision-submit-btn"
          disabled={!decisionText.trim()}
          onClick={handleSubmit}
          style={{
            fontSize: T.fsSm, fontWeight: T.fwBold, padding: "7px 14px",
            borderRadius: 8, border: `1px solid ${T.reviewLeaseChipFg}`,
            background: T.reviewLeaseChipFg, color: T.white,
            cursor: decisionText.trim() ? "pointer" : "not-allowed",
            opacity: decisionText.trim() ? 1 : 0.5,
          }}
        >
          Log &amp; resolve
        </button>
        <button
          data-testid="decision-cancel-btn"
          onClick={onCancel}
          style={{
            fontSize: T.fsSm, padding: "7px 12px",
            borderRadius: 8, border: "1px solid var(--v2-line)",
            background: "transparent", color: "var(--v2-mid)", cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Shared action button helper ──────────────────────────────────────────────

function ActionBtn({ label, onClick, primary = false, disabled = false, testId, purple = false }) {
  const bg     = purple ? "var(--v2-purple)" : primary ? T.reviewLeaseChipFg : T.white;
  const border = purple ? "var(--v2-purple)" : primary ? T.reviewLeaseChipFg : "var(--v2-line)";
  const color  = (primary || purple) ? T.white : "var(--v2-mid)";
  return (
    <button
      data-testid={testId}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onClick(e); }}
      style={{
        fontSize: T.fsSm, fontWeight: T.fwBold, padding: "7px 13px",
        borderRadius: 8, border: `1px solid ${border}`,
        background: bg, color,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );
}

// ─── Decision sub-choice picker ───────────────────────────────────────────────

function DecisionSubchoicePicker({ item, onResolve, resolving }) {
  const [showFreeText, setShowFreeText] = useState(false);

  const handleOutcome = (outcome) => {
    onResolve(item.id, "log_decision", {
      verb:       "log_decision",
      decision:   outcome,
      ownerName:  null,
      targetDate: null,
      entityType: item.subject_type ?? "lease",
      entityId:   item.subject_id ?? item.id,
    });
  };

  if (showFreeText) {
    return (
      <DecisionForm
        item={item}
        onSubmit={(data) => { setShowFreeText(false); onResolve(item.id, "log_decision", data); }}
        onCancel={() => setShowFreeText(false)}
      />
    );
  }

  return (
    <div data-testid={`decision-subchoice-${item.id}`} style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
      {DECISION_OUTCOMES.map(({ label, outcome }) => (
        <ActionBtn
          key={outcome}
          label={label}
          primary={outcome === "Renew" || outcome === "Exit"}
          purple={outcome === "Exit" || outcome === "Renew"}
          disabled={!!resolving}
          testId={`decision-${outcome.toLowerCase()}-btn-${item.id}`}
          onClick={() => handleOutcome(outcome)}
        />
      ))}
      <ActionBtn
        label="Add notes…"
        disabled={!!resolving}
        testId={`decision-notes-btn-${item.id}`}
        onClick={() => setShowFreeText(true)}
      />
    </div>
  );
}

// ─── Per-type primary actions ─────────────────────────────────────────────────

function TypePrimaryActions({ item, onResolve, resolving }) {
  const [showAssignInput, setShowAssignInput] = useState(false);
  const [assignText,      setAssignText]      = useState(item.owner_id ?? "");
  const isDisabled = !!resolving;

  switch (item.type) {
    case "decision":
      return (
        <DecisionSubchoicePicker
          item={item}
          onResolve={onResolve}
          resolving={resolving}
        />
      );

    case "action":
      return (
        <>
          <ActionBtn
            label="▶ Start"
            purple
            disabled={isDisabled}
            testId={`action-start-btn-${item.id}`}
            onClick={() => {
              // Launch the workflow entry-point if suggested, else just resolve.
              const workflowAction = item.suggested_actions?.find((a) => a.kind === "workflow");
              if (workflowAction?.url) {
                window.location.href = workflowAction.url;
              } else {
                onResolve(item.id, "mark_done", { resolution: "Workflow started" });
              }
            }}
          />
          <ActionBtn
            label="Mark done"
            primary
            disabled={isDisabled}
            testId={`action-done-btn-${item.id}`}
            onClick={() => onResolve(item.id, "mark_done")}
          />
        </>
      );

    case "review":
      return (
        <ActionBtn
          label="Mark reviewed"
          primary
          disabled={isDisabled}
          testId={`review-done-btn-${item.id}`}
          onClick={() => onResolve(item.id, "mark_reviewed")}
        />
      );

    case "issue":
      return (
        <>
          {showAssignInput ? (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input
                data-testid={`issue-assign-input-${item.id}`}
                type="text"
                placeholder="Assignee email"
                value={assignText}
                onChange={(e) => setAssignText(e.target.value)}
                style={{ fontSize: T.fsSm, padding: "6px 10px", borderRadius: 8, border: "1px solid var(--v2-line)", width: 180 }}
              />
              <ActionBtn
                label="Save"
                primary
                disabled={!assignText.trim()}
                testId={`issue-assign-save-btn-${item.id}`}
                onClick={() => {
                  setShowAssignInput(false);
                  onResolve(item.id, "mark_resolved", { resolution: `Assigned to ${assignText.trim()}` });
                }}
              />
              <ActionBtn
                label="Cancel"
                testId={`issue-assign-cancel-btn-${item.id}`}
                onClick={() => setShowAssignInput(false)}
              />
            </div>
          ) : (
            <>
              <ActionBtn
                label="Assign"
                disabled={isDisabled}
                testId={`issue-assign-btn-${item.id}`}
                onClick={() => setShowAssignInput(true)}
              />
              <ActionBtn
                label="Mark resolved"
                primary
                disabled={isDisabled}
                testId={`issue-resolved-btn-${item.id}`}
                onClick={() => onResolve(item.id, "mark_resolved")}
              />
              {item.owner_id && (
                <ActionBtn
                  label="Mark verified"
                  disabled={isDisabled}
                  testId={`issue-verified-btn-${item.id}`}
                  onClick={() => onResolve(item.id, "mark_resolved", { resolution: "Verified" })}
                />
              )}
            </>
          )}
        </>
      );

    case "signal":
      return (
        <>
          <ActionBtn
            label="Acknowledge"
            primary
            disabled={isDisabled}
            testId={`signal-ack-btn-${item.id}`}
            onClick={() => onResolve(item.id, "acknowledge")}
          />
          <ActionBtn
            label="Escalate"
            disabled={isDisabled}
            testId={`signal-escalate-btn-${item.id}`}
            onClick={() => onResolve(item.id, "escalate")}
          />
        </>
      );

    case "flag":
      return (
        <ActionBtn
          label="Fix data"
          primary
          disabled={isDisabled}
          testId={`flag-fix-btn-${item.id}`}
          onClick={() => {
            const workflowAction = item.suggested_actions?.find((a) => a.kind === "workflow");
            if (workflowAction?.url) {
              window.location.href = workflowAction.url;
            } else {
              onResolve(item.id, "mark_fixed");
            }
          }}
        />
      );

    default:
      return (
        <ActionBtn
          label="Resolve"
          primary
          disabled={isDisabled}
          testId={`resolve-btn-${item.id}`}
          onClick={() => onResolve(item.id, null)}
        />
      );
  }
}

// ─── Row ──────────────────────────────────────────────────────────────────────

function ItemRow({
  item,
  expanded,
  onToggle,
  onResolve,
  onSnooze,
  onDismiss,
  resolving,
  snoozing,
  isEditor,
  compact = false,
}) {
  const [showSnoozePicker, setShowSnoozePicker] = useState(false);

  // Reset inline forms when row collapses
  useEffect(() => {
    if (!expanded) {
      setShowSnoozePicker(false);
    }
  }, [expanded]);

  const handleSnoozeClick = (e) => {
    e.stopPropagation();
    setShowSnoozePicker((prev) => !prev);
  };

  const handleSnoozeConfirm = (isoDate) => {
    setShowSnoozePicker(false);
    onSnooze(item.id, isoDate);
  };

  const firstFactor = item.ranking_factors?.[0];

  return (
    <div
      data-testid={`focus-item-row-${item.id}`}
      style={{ borderTop: "1px solid var(--v2-line)" }}
    >
      {/* Main row */}
      <div
        role="button"
        tabIndex={0}
        data-testid={`focus-item-toggle-${item.id}`}
        data-list-row=""
        onClick={onToggle}
        onKeyDown={(e) => e.key === "Enter" && onToggle()}
        style={{
          display: "flex", alignItems: "center", gap: 13,
          padding: compact ? "6px 16px" : "12px 16px", cursor: "pointer",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "var(--v2-hair)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
        // No quick-action cluster (pin / snooze / mute / more / delete) is rendered on
        // row hover by design — the drawer is the sole action surface for this page.
      >
        <TypePill type={item.type} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: T.fwBold, fontSize: T.fsBase, display: "flex", alignItems: "center", gap: 7 }}>
            <span>{item.title}</span>
            {item.data_confidence === "low" && (
              <span data-testid="low-confidence-badge" style={{ fontSize: T.fsMin5, fontWeight: T.fwBold, color: "var(--v2-amber)", background: "var(--v2-amber-tint)", padding: "1px 6px", borderRadius: 5, letterSpacing: "0.03em" }}>
                LOW CONFIDENCE
              </span>
            )}
            {item.playbook_run_id && (
              <a
                href={`/playbooks/runs/${item.playbook_run_id}`}
                data-testid="playbook-source-chip"
                onClick={(e) => e.stopPropagation()}
                style={{
                  fontSize: T.fsMin5, fontWeight: T.fwBold,
                  color: T.reviewLeaseChipFg, background: T.blueSoft,
                  padding: "1px 6px", borderRadius: 5, letterSpacing: "0.03em",
                  textDecoration: "none", flexShrink: 0,
                }}
              >
                Playbook →
              </a>
            )}
          </div>
          <div style={{ fontSize: T.fsXs, color: "var(--v2-mid)", marginTop: 2 }}>
            <span style={{ color: T.reviewLeaseChipFg, fontWeight: T.fwSemibold }}>{item.subject_id}</span>
            {" · "}
            <span style={{ textTransform: "capitalize" }}>{item.subject_type}</span>
          </div>
          {item.context && (
            <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 3 }}>
              {item.context}
            </div>
          )}
          {firstFactor && (
            <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 3, display: "flex", alignItems: "center", gap: 4 }}>
              <span>⚡</span>
              <span>{firstFactor.label} · {firstFactor.value}</span>
            </div>
          )}
        </div>

        <AvatarOwner ownerId={item.owner_id} />
        <DueLabel dueAt={item.due_at} />
        <UrgencyScore score={item.score} />
        <span style={{ color: "var(--v2-mid)", flexShrink: 0, fontSize: T.fsSm, transition: "transform 0.15s", transform: expanded ? "rotate(90deg)" : "none" }}>›</span>
      </div>

      {/* Expand zone */}
      {expanded && (
        <div
          data-testid={`focus-item-expand-${item.id}`}
          style={{
            padding: "0 16px 14px 93px",
            borderTop: "1px dashed var(--v2-line)",
            background: T.planRowBg,
          }}
        >
          {/* Why Radar ranked this — renders for all types; shows empty state when no factors */}
          <div style={{
            background: "var(--v2-card)", border: "1px solid var(--v2-line)",
            borderRadius: 10, padding: "11px 13px", marginTop: 12,
          }}>
            <div data-testid="ranking-factors-header" style={{ fontSize: T.fsXxxs5, letterSpacing: "0.05em", textTransform: "uppercase", fontWeight: T.fwBold, color: "var(--v2-mid)", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
              ⚡ Why Radar ranked this
            </div>
            {(item.ranking_factors ?? []).length > 0 ? (
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: T.fsSm, color: "var(--v2-mid)" }}>
                {(item.ranking_factors ?? []).map((f, i) => (
                  <span key={i}><strong style={{ color: "var(--v2-dark)" }}>{f.label}</strong> · {f.value}</span>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: T.fsSm5, color: "var(--v2-mid)", fontStyle: "italic" }}>
                No ranking factors available.
              </div>
            )}
          </div>

          {/* Description */}
          {item.resolution == null && (
            <div style={{ fontSize: T.fsSm5, color: "var(--v2-mid)", marginTop: 11 }}>
              {item.subject_type === "lease" ? `Subject: ${item.subject_id}` : `${item.subject_type}: ${item.subject_id}`}
              {item.consequence_usd != null && ` · $${(Number(item.consequence_usd) / 1_000_000).toFixed(1)}m/yr`}
            </div>
          )}

          {/* Primary action area — type-specific; visible to editors only.
              G-34-S3: playbook-action rows live in the actions table, not
              focus_items — lifecycle API calls would 404.  Show a redirect
              link to the run instead. */}
          {item.is_playbook_action ? (
            <div
              data-testid={`playbook-action-nav-${item.id}`}
              style={{ marginTop: 12 }}
            >
              <a
                href={`/playbooks/runs/${item.playbook_run_id}`}
                data-testid={`open-playbook-run-btn-${item.id}`}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  fontSize: T.fsSm, fontWeight: T.fwBold, padding: "7px 13px",
                  borderRadius: 8, border: "none",
                  background: "var(--v2-accent)", color: "var(--v2-card)",
                  textDecoration: "none",
                }}
              >
                Open Playbook run →
              </a>
            </div>
          ) : (
            <>
              {isEditor && (
                <div
                  data-testid={`primary-actions-${item.id}`}
                  style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}
                >
                  <TypePrimaryActions
                    item={item}
                    onResolve={onResolve}
                    resolving={resolving}
                  />
                </div>
              )}

              {/* Secondary action row: Open record + Snooze + Dismiss — universals */}
              <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                {/* Open record — visible to all roles */}
                <button
                  data-testid={`open-record-btn-${item.id}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    fontSize: T.fsSm, fontWeight: T.fwBold, padding: "7px 13px",
                    borderRadius: 8, border: `1px solid ${T.blueSoftBorder}`,
                    color: T.reviewLeaseChipFg, background: T.white, cursor: "pointer",
                  }}
                >
                  Open record
                </button>

                {isEditor && (
                  <button
                    data-testid={`snooze-btn-${item.id}`}
                    disabled={!!snoozing}
                    onClick={handleSnoozeClick}
                    style={{
                      fontSize: T.fsSm, fontWeight: T.fwBold, padding: "7px 13px",
                      borderRadius: 8, border: "1px solid var(--v2-line)",
                      color: "var(--v2-mid)", background: T.white,
                      cursor: snoozing ? "not-allowed" : "pointer",
                      opacity: snoozing ? 0.6 : 1,
                    }}
                  >
                    Snooze
                  </button>
                )}

                {isEditor && (
                  <button
                    data-testid={`dismiss-btn-${item.id}`}
                    onClick={(e) => { e.stopPropagation(); onDismiss(item.id); }}
                    style={{
                      fontSize: T.fsSm, fontWeight: T.fwBold, padding: "7px 13px",
                      borderRadius: 8, border: "1px solid var(--v2-line)",
                      color: "var(--v2-mid)", background: "transparent", cursor: "pointer",
                    }}
                  >
                    Dismiss
                  </button>
                )}
              </div>
            </>
          )}

          {/* Snooze picker */}
          {showSnoozePicker && (
            <SnoozePicker
              onSnooze={handleSnoozeConfirm}
              onCancel={() => setShowSnoozePicker(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Band column header (Type / Item / Owner / Due / Urgency) ─────────────────
//
// Sits at the top of each per-band card with a subtle rounded-top treatment,
// matching the comp's `.cols` row. Column widths mirror the flex widths used
// by ItemRow so labels align with row content.

function BandColumnHeader() {
  return (
    <div
      role="row"
      style={{
        display: "grid",
        gridTemplateColumns: "72px 1fr 112px 82px 52px 20px",
        gap: 13,
        padding: "9px 16px",
        background: "var(--v2-tint)",
        borderBottom: "1px solid var(--v2-line)",
        fontSize: T.fsXxxs,
        fontWeight: T.fwBold,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: "var(--v2-mid)",
        borderRadius: "8px 8px 0 0",
      }}
    >
      <span>Type</span>
      <span>Item</span>
      <span>Owner</span>
      <span style={{ textAlign: "right" }}>Due</span>
      <span style={{ textAlign: "right" }}>Urgency</span>
      <span />
    </div>
  );
}

// ─── Band card ────────────────────────────────────────────────────────────────
//
// Renders only the column header + item rows. The band-label pill header lives
// outside this component (in the main render loop) so each band gets its own
// standalone rounded card with an external pill-header row, matching the
// Leases Timeline group treatment.

function BandCard({ band, items, expandedId, onToggle, onResolve, onSnooze, onDismiss, resolving, snoozing, isEditor, compact = false }) {
  const isActNow = band.key === "act_now";
  const isEmpty  = items.length === 0;

  if (isEmpty && isActNow) {
    return (
      <>
        <BandColumnHeader />
        <div data-testid="act-now-empty" style={{ padding: 22, textAlign: "center", color: "var(--v2-mid)", fontSize: T.fsSm5, background: T.white }}>
          You're clear — Radar is watching.
        </div>
      </>
    );
  }

  if (isEmpty) return null;

  return (
    <OverflowGrid minWidth={640} data-testid={`band-${band.key}`}>
      <BandColumnHeader />
      {items.map((item) => (
        <ItemRow
          key={item.id}
          item={item}
          expanded={expandedId === item.id}
          onToggle={() => onToggle(item.id)}
          onResolve={onResolve}
          onSnooze={onSnooze}
          onDismiss={onDismiss}
          isEditor={isEditor}
          resolving={resolving === item.id}
          snoozing={snoozing === item.id}
          compact={compact}
        />
      ))}
    </OverflowGrid>
  );
}

// ─── Snoozed Section ──────────────────────────────────────────────────────────

function SnoozedSection({ items, onWakeUp, loading, error, sectionRef, open, onToggle }) {
  if (items.length === 0 && !loading && !error) return null;

  function fmtSnoozeUntil(iso) {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch { return iso.slice(0, 10); }
  }

  return (
    <div ref={sectionRef} data-testid="snoozed-section" style={{ marginTop: 24 }}>
      <button
        data-testid="snoozed-toggle"
        onClick={onToggle}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          fontSize: T.fsXxs5, letterSpacing: "0.06em", textTransform: "uppercase",
          fontWeight: T.fwDisplay, color: "var(--v2-amber)",
          background: "none", border: "none", cursor: "pointer", padding: 0,
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--v2-amber)", display: "inline-block" }} />
        Snoozed
        <span style={{ fontWeight: T.fwBold }}>· {items.length}</span>
        <span style={{ fontSize: T.fsSm, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
      </button>

      {open && (
        <div style={{ background: "var(--v2-card)", border: "1px solid var(--v2-line)", borderRadius: 14, overflow: "hidden", marginTop: 8, boxShadow: "0 4px 16px rgba(15,23,42,.04)" }}>
          {loading && (
            <div style={{ padding: 18, textAlign: "center", color: "var(--v2-mid)", fontSize: T.fsSm5 }}>Loading…</div>
          )}
          {!loading && error && (
            <div
              data-testid="snoozed-error"
              style={{ padding: "12px 16px", color: "var(--v2-red)", fontSize: T.fsSm5, display: "flex", alignItems: "center", gap: 6 }}
            >
              <span>⚠</span>
              <span>Could not load snoozed items — {error}</span>
            </div>
          )}
          {!loading && !error && items.length === 0 && (
            <div style={{ padding: 18, textAlign: "center", color: "var(--v2-mid)", fontSize: T.fsSm5 }}>No snoozed items.</div>
          )}
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`snoozed-row-${item.id}`}
              style={{
                display: "flex", alignItems: "center", gap: 12, padding: "10px 16px",
                borderTop: "1px solid var(--v2-line)",
              }}
            >
              <TypePill type={item.type} />
              <span style={{ flex: 1, fontSize: T.fsSm5, color: "var(--v2-mid)" }}>{item.title}</span>
              {item.snooze_until && (
                <span style={{ fontSize: T.fsXs, color: "var(--v2-amber)", whiteSpace: "nowrap", flexShrink: 0 }}>
                  Until {fmtSnoozeUntil(item.snooze_until)}
                </span>
              )}
              <button
                data-testid={`wakeup-btn-${item.id}`}
                onClick={() => onWakeUp(item.id)}
                style={{
                  fontSize: T.fsSm, fontWeight: T.fwBold, padding: "5px 11px",
                  borderRadius: 8, border: `1px solid var(--v2-amber)`,
                  color: "var(--v2-amber)", background: T.white, cursor: "pointer",
                  whiteSpace: "nowrap", flexShrink: 0,
                }}
              >
                Wake up
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Dismissed Section ────────────────────────────────────────────────────────

function DismissedSection({ items, onRestore, loading }) {
  const [open, setOpen] = useState(false);

  if (items.length === 0 && !loading) return null;

  return (
    <div data-testid="dismissed-section" style={{ marginTop: 32 }}>
      <button
        data-testid="dismissed-toggle"
        onClick={() => setOpen((p) => !p)}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          fontSize: T.fsXxs5, letterSpacing: "0.06em", textTransform: "uppercase",
          fontWeight: T.fwDisplay, color: "var(--v2-mid)",
          background: "none", border: "none", cursor: "pointer", padding: 0,
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--v2-mid)", display: "inline-block" }} />
        Dismissed
        <span style={{ fontWeight: T.fwBold }}>· {items.length}</span>
        <span style={{ fontSize: T.fsSm, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
      </button>

      {open && (
        <div style={{ background: "var(--v2-card)", border: "1px solid var(--v2-line)", borderRadius: 14, overflow: "hidden", marginTop: 8, boxShadow: "0 4px 16px rgba(15,23,42,.04)" }}>
          {loading && (
            <div style={{ padding: 18, textAlign: "center", color: "var(--v2-mid)", fontSize: T.fsSm5 }}>Loading…</div>
          )}
          {!loading && items.length === 0 && (
            <div style={{ padding: 18, textAlign: "center", color: "var(--v2-mid)", fontSize: T.fsSm5 }}>No dismissed items.</div>
          )}
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`dismissed-row-${item.id}`}
              style={{
                display: "flex", alignItems: "center", gap: 12, padding: "10px 16px",
                borderTop: "1px solid var(--v2-line)",
              }}
            >
              <TypePill type={item.type} />
              <span style={{ flex: 1, fontSize: T.fsSm5, color: "var(--v2-mid)", fontStyle: "italic" }}>{item.title}</span>
              <button
                data-testid={`restore-btn-${item.id}`}
                onClick={() => onRestore(item.id)}
                style={{
                  fontSize: T.fsSm, fontWeight: T.fwBold, padding: "5px 11px",
                  borderRadius: 8, border: `1px solid ${T.reviewLeaseChipFg}`,
                  color: T.reviewLeaseChipFg, background: T.white, cursor: "pointer",
                }}
              >
                Restore
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Admin Radar Settings panel ───────────────────────────────────────────────

function AdminRadarSettingsPanel() {
  const [open,        setOpen]        = useState(false);
  const [months,      setMonths]      = useState("");
  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [error,       setError]       = useState(null);
  // pendingRun: true after a successful save until the user clicks "Run now"
  const [pendingRun,  setPendingRun]  = useState(false);
  const [running,     setRunning]     = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    fetch("/api/admin/radar-settings", { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        // New multi-key format: d.timing.lease_window_lead_months (default 12)
        setMonths(String(d.timing?.lease_window_lead_months ?? 12));
      })
      .catch(() => { setError("Could not load settings"); })
      .finally(() => setLoading(false));
  }, [open]);

  const handleSave = async () => {
    const parsed = parseInt(months, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError("Months must be a positive number");
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const r = await fetch("/api/admin/radar-settings", {
        method:  "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        // Write to radar_timing.lease_window_lead_months (the canonical key read by loadRadarConfig)
        body:    JSON.stringify({ timing: { lease_window_lead_months: parsed } }),
      });
      if (!r.ok) throw new Error("Save failed");
      setSaved(true);
      setPendingRun(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const r = await fetch("/api/focus-items/run", {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error("Run failed");
      const data = await r.json().catch(() => ({}));
      setPendingRun(false);
      const inserted = data.inserted ?? 0;
      const updated  = data.updated  ?? 0;
      showToast(`Emitter ran — ${inserted} new, ${updated} updated`);
    } catch {
      showToast("Emitter run failed", { type: "error" });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div data-testid="admin-radar-settings" style={{ marginTop: 32 }}>
      <button
        data-testid="admin-radar-settings-toggle"
        onClick={() => setOpen((p) => !p)}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          fontSize: T.fsXxs5, letterSpacing: "0.06em", textTransform: "uppercase",
          fontWeight: T.fwDisplay, color: "var(--v2-mid)",
          background: "none", border: "none", cursor: "pointer", padding: 0,
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--v2-mid)", display: "inline-block" }} />
        Radar settings
        <span style={{ fontSize: T.fsSm, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
      </button>

      {open && (
        <div style={{ background: "var(--v2-card)", border: "1px solid var(--v2-line)", borderRadius: 14, padding: "16px 18px", marginTop: 8, boxShadow: "0 4px 16px rgba(15,23,42,.04)" }}>
          <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: "var(--v2-dark)", marginBottom: 12 }}>
            Signal &amp; noise
          </div>
          {loading ? (
            <div style={{ fontSize: T.fsSm5, color: "var(--v2-mid)" }}>Loading…</div>
          ) : (
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <label style={{ fontSize: T.fsSm5, color: "var(--v2-mid)", display: "flex", alignItems: "center", gap: 8 }}>
                Lease decision window · months
                <input
                  data-testid="radar-notice-window-input"
                  type="number"
                  min="1"
                  step="1"
                  value={months}
                  onChange={(e) => setMonths(e.target.value)}
                  style={{ width: 80, fontSize: T.fsSm5, padding: "5px 8px", borderRadius: 7, border: "1px solid var(--v2-line)", background: "var(--v2-card)" }}
                />
              </label>
              <button
                data-testid="radar-notice-window-save"
                disabled={saving}
                onClick={handleSave}
                style={{
                  fontSize: T.fsSm, fontWeight: T.fwBold, padding: "6px 14px",
                  borderRadius: 8, border: `1px solid ${T.reviewLeaseChipFg}`,
                  background: T.reviewLeaseChipFg, color: T.white,
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? "Saving…" : "Save"}
              </button>
              {saved && <span style={{ fontSize: T.fsSm5, color: "var(--v2-green)", fontWeight: T.fwBold }}>✓ Saved</span>}
            </div>
          )}
          {error && (
            <div style={{ fontSize: T.fsSm5, color: "var(--v2-red)", marginTop: 8 }}>{error}</div>
          )}

          {/* Re-run prompt — appears after a successful save until the user runs */}
          {pendingRun && (
            <div
              data-testid="radar-rerun-prompt"
              style={{
                display: "flex", alignItems: "center", gap: 10,
                marginTop: 12, padding: "10px 12px",
                background: "var(--v2-amber-tint)", borderRadius: 9,
                border: "1px solid var(--v2-amber)",
              }}
            >
              <span style={{ fontSize: T.fsSm5, color: "var(--v2-dark)", flex: 1 }}>
                Settings saved — re-run emitter to apply
              </span>
              <button
                data-testid="radar-run-now-btn"
                disabled={running}
                onClick={handleRunNow}
                style={{
                  fontSize: T.fsSm, fontWeight: T.fwBold, padding: "5px 12px",
                  borderRadius: 8, border: `1px solid ${T.reviewLeaseChipFg}`,
                  background: T.reviewLeaseChipFg, color: T.white,
                  cursor: running ? "not-allowed" : "pointer",
                  opacity: running ? 0.6 : 1, whiteSpace: "nowrap",
                }}
              >
                {running ? "Running…" : "Run now"}
              </button>
            </div>
          )}

          <div style={{ fontSize: T.fsXs, color: "var(--v2-mid)", marginTop: 10 }}>
            Critical dates further out than this window will not appear in the feed.
            Default: 12 months.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function RadarSpinePage({ currentRole }) {
  const featureFlags = useFeatureFlags();
  const decisionsRegisterEnabled = featureFlags[RADAR_DECISIONS_FLAG] !== false;
  const isEditor = currentRole === "editor" || currentRole === "admin";
  const isAdmin  = currentRole === "admin";

  // Initialise scope from localStorage (persisted preference)
  const [items,           setItems]           = useState([]);
  const [counts,          setCounts]          = useState({});
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [expandedId,      setExpandedId]      = useState(null);
  const [resolving,       setResolving]       = useState(null);
  const [snoozing,        setSnoozing]        = useState(null);
  const [typeFilter,      setTypeFilter]      = useState(null);
  const [statFilter,      setStatFilter]      = useState(null);
  const [scope,           setScope]           = useState(() => {
    try { return localStorage.getItem(SCOPE_PREF_KEY) || "all"; } catch { return "all"; }
  });
  const [sort,            setSort]            = useState("priority");
  const [uiPrefs,         setUiPrefs]         = useUiPrefs(UI_PREFS_RADAR_V1, { compact: false });
  const compact    = uiPrefs.compact ?? false;
  const setCompact = useCallback(() => setUiPrefs((p) => ({ ...p, compact: !p.compact })), [setUiPrefs]);
  const [filterSelections, setFilterSelections] = useState({});
  const [search,          setSearch]          = useState("");
  const [dismissedItems,  setDismissedItems]  = useState([]);
  const [dismissedLoading,setDismissedLoading]= useState(false);
  const [snoozedItems,    setSnoozedItems]    = useState([]);
  const [snoozedLoading,  setSnoozedLoading]  = useState(false);
  const [snoozedError,    setSnoozedError]    = useState(null);
  const [snoozedOpen,     setSnoozedOpen]     = useState(false);
  const snoozedSectionRef = useRef(null);

  // ── Scope persistence ────────────────────────────────────────────────────────

  const handleSetScope = useCallback((s) => {
    setScope(s);
    try { localStorage.setItem(SCOPE_PREF_KEY, s); } catch { /* ignore */ }
  }, []);

  // ── Data loading ─────────────────────────────────────────────────────────────

  const loadFeed = useCallback(async () => {
    try {
      const data = await getFocusItemsFeed({
        mine: scope === "mine",
        type: typeFilter,
        sort: sort === "due_soonest" ? "due_soonest" : null,
      });
      setItems(data.items ?? []);
      setCounts(data.counts ?? {});
    } catch (err) {
      log.warn("RadarSpinePage: feed load failed", { err: String(err) });
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, [scope, typeFilter, sort]);

  const loadDismissed = useCallback(async () => {
    if (!isEditor) return; // viewers cannot access the dismissed feed (403)
    setDismissedLoading(true);
    try {
      const data = await getDismissedFocusItems();
      setDismissedItems(data.items ?? []);
    } catch (err) {
      log.warn("RadarSpinePage: dismissed load failed", { err: String(err) });
    } finally {
      setDismissedLoading(false);
    }
  }, [isEditor]);

  const loadSnoozed = useCallback(async () => {
    if (!isEditor) return; // editor-gated endpoint
    setSnoozedLoading(true);
    setSnoozedError(null);
    try {
      const data = await getSnoozedFocusItems();
      setSnoozedItems(data.items ?? []);
    } catch (err) {
      log.warn("RadarSpinePage: snoozed load failed", { err: String(err) });
      setSnoozedError(String(err));
    } finally {
      setSnoozedLoading(false);
    }
  }, [isEditor]);

  // Run emitters once on mount only.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { await runFocusItemEmitters(); } catch (_) { /* best-effort */ }
      if (!cancelled) {
        await loadFeed();
        await loadDismissed();
        await loadSnoozed();
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally mount-only

  // Reload feed when filters change
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; return; }
    loadFeed();
  }, [loadFeed]);

  // ── Resolve action ───────────────────────────────────────────────────────────

  const handleResolve = useCallback(async (id, verb = null, extraData = null) => {
    setResolving(id);
    // Optimistically remove from active feed
    const removed = items.find((i) => i.id === id);
    setItems((prev) => prev.filter((i) => i.id !== id));
    setExpandedId(null);

    try {
      await resolveFocusItem(id, verb, extraData);
      const toastMsg = verb === "log_decision"  ? "Decision logged · resolved"
                     : verb === "acknowledge"    ? "Signal acknowledged · watermark stored"
                     : verb === "escalate"       ? "Escalated · logged to audit_log"
                     : "Resolved · logged to audit_log";
      showToast(
        toastMsg,
        { action: { label: "Undo", onClick: async () => {
          // Undo: call unresolve on the server (audited state transition), then reload
          try {
            await unresolveFocusItem(id);
            await loadFeed();
            showToast("Restored");
          } catch (err) {
            log.warn("RadarSpinePage: undo resolve failed", { err: String(err) });
            showToast(`Could not restore: ${err.message ?? String(err)}`, { type: "error" });
          }
        } } },
      );
    } catch (err) {
      log.warn("RadarSpinePage: resolve failed", { err: String(err) });
      // Restore on failure and surface the error
      if (removed) setItems((prev) => [removed, ...prev]);
      showToast(`Could not resolve: ${err.message ?? String(err)}`, { type: "error" });
    } finally {
      setResolving(null);
    }
  }, [items, loadFeed]);

  // ── Snooze action ────────────────────────────────────────────────────────────

  const handleSnooze = useCallback(async (id, snoozeUntil) => {
    setSnoozing(id);
    const removed = items.find((i) => i.id === id);
    setItems((prev) => prev.filter((i) => i.id !== id));
    setExpandedId(null);

    try {
      await snoozeFocusItem(id, snoozeUntil);
      // Optimistically add to the snoozed list
      if (removed) {
        setSnoozedItems((prev) => [
          { ...removed, state: "snoozed", snooze_until: snoozeUntil },
          ...prev,
        ]);
      }
      showToast(
        "Snoozed · will return when the time arrives",
        { action: { label: "Undo", onClick: async () => {
          try {
            await unsnoozeFocusItem(id);
            setSnoozedItems((prev) => prev.filter((i) => i.id !== id));
            await loadFeed();
            showToast("Snooze cleared");
          } catch (err) {
            log.warn("RadarSpinePage: unsnooze failed", { err: String(err) });
            showToast(`Could not clear snooze: ${err.message ?? String(err)}`, { type: "error" });
          }
        } } },
      );
    } catch (err) {
      log.warn("RadarSpinePage: snooze failed", { err: String(err) });
      if (removed) setItems((prev) => [removed, ...prev]);
      showToast(`Could not snooze: ${err.message ?? String(err)}`, { type: "error" });
    } finally {
      setSnoozing(null);
    }
  }, [items, loadFeed]);

  // ── Dismiss action ───────────────────────────────────────────────────────────

  const handleDismiss = useCallback(async (id) => {
    const removed = items.find((i) => i.id === id);
    setItems((prev) => prev.filter((i) => i.id !== id));
    setExpandedId(null);

    try {
      const result = await dismissFocusItem(id);
      // Add to dismissed list immediately
      if (removed) {
        setDismissedItems((prev) => [
          { ...removed, state: "dismissed", deleted_at: result.deleted_at },
          ...prev,
        ]);
      }
      showToast(
        "Dismissed · recoverable in Trash",
        { action: { label: "Undo", onClick: async () => {
          try {
            await restoreFocusItem(id);
            setDismissedItems((prev) => prev.filter((i) => i.id !== id));
            await loadFeed();
            showToast("Restored");
          } catch (err) {
            log.warn("RadarSpinePage: restore (undo dismiss) failed", { err: String(err) });
            showToast(`Could not restore: ${err.message ?? String(err)}`, { type: "error" });
          }
        } } },
      );
    } catch (err) {
      log.warn("RadarSpinePage: dismiss failed", { err: String(err) });
      if (removed) setItems((prev) => [removed, ...prev]);
      showToast(`Could not dismiss: ${err.message ?? String(err)}`, { type: "error" });
    }
  }, [items, loadFeed]);

  // ── Wake up action (from snoozed section) ────────────────────────────────────

  const handleWakeUp = useCallback(async (id) => {
    const removed = snoozedItems.find((i) => i.id === id);
    setSnoozedItems((prev) => prev.filter((i) => i.id !== id));

    try {
      await unsnoozeFocusItem(id);
      await loadFeed();
      showToast("Snooze cleared · back in the active feed");
    } catch (err) {
      log.warn("RadarSpinePage: wake up failed", { err: String(err) });
      if (removed) setSnoozedItems((prev) => [removed, ...prev]);
      showToast(`Could not clear snooze: ${err.message ?? String(err)}`, { type: "error" });
    }
  }, [snoozedItems, loadFeed]);

  // ── Restore action (from dismissed section) ──────────────────────────────────

  const handleRestore = useCallback(async (id) => {
    const removed = dismissedItems.find((i) => i.id === id);
    setDismissedItems((prev) => prev.filter((i) => i.id !== id));

    try {
      await restoreFocusItem(id);
      await loadFeed();
      showToast("Restored · back in the active feed");
    } catch (err) {
      log.warn("RadarSpinePage: restore failed", { err: String(err) });
      if (removed) setDismissedItems((prev) => [removed, ...prev]);
      showToast(`Could not restore: ${err.message ?? String(err)}`, { type: "error" });
    }
  }, [dismissedItems, loadFeed]);

  // ── Filtering ────────────────────────────────────────────────────────────────

  const filteredItems = useMemo(() => {
    let list = items;
    const q = search.trim();
    if (q) list = list.filter((i) => matchesFoldedQuery(q, i.title, i.subject_id));
    if (statFilter === "over")       list = list.filter((i) => i.due_at && new Date(i.due_at) < new Date());
    if (statFilter === "un")         list = list.filter((i) => !i.owner_id);
    if (statFilter === "act_now")    list = list.filter((i) => i.band === "act_now");
    if (statFilter === "this_cycle") list = list.filter((i) => i.band === "this_cycle");
    if (statFilter === "focus")      list = list.filter((i) => i.in_focus);
    // ConsolidatedFilter multi-select type filter (empty = show all)
    const typeSelections = filterSelections.type;
    if (typeSelections && typeSelections.length > 0) {
      list = list.filter((i) => typeSelections.includes(i.type));
    }
    return list;
  }, [items, search, statFilter, filterSelections]);

  const itemsByBand = useMemo(() => {
    const map = {};
    for (const b of BANDS) map[b.key] = [];
    for (const item of filteredItems) {
      const band = item.band ?? "keep_watch";
      if (map[band]) map[band].push(item);
    }
    return map;
  }, [filteredItems]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleToggleRow  = useCallback((id) => setExpandedId((prev) => prev === id ? null : id), []);
  const handleStatFilter = useCallback((k) => setStatFilter((prev) => prev === k ? null : k), []);

  // ── Live counts ───────────────────────────────────────────────────────────────

  const liveCounts = useMemo(() => {
    const now = new Date();
    const cutoff14 = new Date(now.getTime() + 14 * 86_400_000);
    let overdue = 0, due_soon = 0, decisions = 0, issues = 0, unassigned = 0;
    for (const i of items) {
      if (i.due_at) {
        const d = new Date(i.due_at);
        if (d < now) overdue++;
        else if (d <= cutoff14) due_soon++;
      }
      if (i.type === "decision") decisions++;
      if (i.type === "issue" || i.type === "flag") issues++;
      if (!i.owner_id) unassigned++;
    }
    return { overdue, due_soon, decisions, issues, unassigned };
  }, [items]);

  // Derived sort objects for SortControl
  const sortPrimary   = useMemo(() => ({ key: sort, dir: "asc" }), [sort]);
  const sortSecondary = useMemo(() => ({ key: null, dir: "asc" }), []);
  const handleSortControlChange = useCallback(({ primary }) => setSort(primary.key), []);

  // ── Render ───────────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div style={{ padding: 32, color: "var(--v2-red)", fontSize: T.fsBase }}>
        Radar feed error: {error}
      </div>
    );
  }

  return (
    <div data-testid="radar-page" style={{ minHeight: "100%" }}>
      <PageHeaderStack>

      {/* Rounded master header — stays visible while the feed scrolls, matching
          the sticky list-header treatment used by Offices and Leases. */}
      <div
        data-testid="radar-page-header"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 20,
          padding: "14px 18px",
          background: "var(--v2-card)",
          border: "1px solid var(--v2-line)",
          borderRadius: 14,
          boxShadow: "var(--v2-shadow-card)",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: T.fsLgX5, fontWeight: T.fwBold, color: "var(--v2-dark)", lineHeight: 1.15 }}>Radar</h1>
          <div style={{ color: "var(--v2-mid)", fontSize: T.fsBase, marginTop: 3 }}>
            Everything that needs a decision, review, or action — ranked by how soon it needs you.
          </div>
        </div>
        <div
          data-testid="radar-ranked-badge"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            flexShrink: 0,
            fontSize: T.fsXs5, color: "var(--v2-mid)", background: "var(--v2-card)",
            border: "1px solid var(--v2-line)", borderRadius: 999, padding: "5px 11px",
          }}
        >
          <span
            data-testid="radar-pulse-dot"
            style={{
              width: 7, height: 7, borderRadius: "50%", background: "var(--v2-green)",
              animation: "radar-pulse 2s infinite",
              display: "inline-block",
            }}
          />
          Ranked by Radar · updated just now
        </div>
      </div>

      {/* KPI tiles — six urgency-colored tiles, each with a plain description.
           Numbers are colored by urgency tier; no "in view" funnel sub-label. */}
      <div style={{ marginBottom: 14 }}>
        <KpiTileRow testId="kpi-tile-row-radar" columns={6}>
          {/* In Focus — primary blue */}
          <div onClick={() => handleStatFilter("focus")} style={{ cursor: "pointer" }}>
            <KpiTile
              testId="kpi-tile-in-focus"
              label="In Focus"
              rawValue={items.filter((i) => i.in_focus).length}
              color="var(--v2-accent)"
              isFiltered={statFilter === "focus"}
              sub="Top-ranked · needs your eyes"
            />
          </div>
          {/* Act Now — amber */}
          <div onClick={() => handleStatFilter("act_now")} style={{ cursor: "pointer" }}>
            <KpiTile
              testId="kpi-tile-act-now"
              label="Act Now"
              rawValue={itemsByBand.act_now?.length ?? 0}
              color="var(--v2-amber)"
              isFiltered={statFilter === "act_now"}
              sub="Overdue or due ≤ 30 days"
            />
          </div>
          {/* This Cycle — neutral */}
          <div onClick={() => handleStatFilter("this_cycle")} style={{ cursor: "pointer" }}>
            <KpiTile
              testId="kpi-tile-this-cycle"
              label="This Cycle"
              rawValue={itemsByBand.this_cycle?.length ?? 0}
              color="var(--v2-mid)"
              isFiltered={statFilter === "this_cycle"}
              sub="Due within 90 days"
            />
          </div>
          {/* Overdue — red */}
          <div onClick={() => handleStatFilter("over")} style={{ cursor: "pointer" }}>
            <KpiTile
              testId="kpi-tile-overdue"
              label="Overdue"
              rawValue={liveCounts.overdue ?? 0}
              color="var(--v2-red)"
              isFiltered={statFilter === "over"}
              sub="Past their due date"
            />
          </div>
          {/* Unassigned — neutral */}
          <div onClick={() => handleStatFilter("un")} style={{ cursor: "pointer" }}>
            <KpiTile
              testId="kpi-tile-unassigned"
              label="Unassigned"
              rawValue={liveCounts.unassigned ?? 0}
              color="var(--v2-mid)"
              isFiltered={statFilter === "un"}
              sub="No owner yet"
            />
          </div>
          {/* Tracked — neutral ink; clicking clears stat filter */}
          <div onClick={() => setStatFilter(null)} style={{ cursor: "pointer" }}>
            <KpiTile
              testId="kpi-tile-tracked"
              label="Tracked"
              rawValue={filteredItems.length}
              isFiltered={statFilter !== null}
              sub="Total items on Radar"
            />
          </div>
        </KpiTileRow>
        {/* Snoozed chip — preserved below tiles when snoozed items exist */}
        {isEditor && snoozedItems.length > 0 && (
          <div
            data-testid="stat-chip-snoozed"
            onClick={() => {
              setSnoozedOpen(true);
              setTimeout(() => {
                snoozedSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 50);
            }}
            style={{
              display: "inline-flex", alignItems: "center", gap: 7, marginTop: 8,
              background: "var(--v2-amber-tint)", border: "1px solid var(--v2-amber)",
              borderRadius: 999, padding: "4px 12px", cursor: "pointer",
              fontSize: T.fsXs, fontWeight: T.fwBold, color: "var(--v2-amber)",
            }}
          >
            {snoozedItems.length} Snoozed →
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 6, flexWrap: "wrap" }}>
        {/* Search — shared SearchInput */}
        <SearchInput
          testId="radar-search"
          value={search}
          onChange={setSearch}
          placeholder="Search items…"
          width={260}
        />

        {/* Site-standard ConsolidatedFilter replacing the custom type-chip row */}
        <ConsolidatedFilter
          testId="radar-filter-btn"
          dimensions={RADAR_FILTER_DIMENSIONS}
          selections={filterSelections}
          onChange={setFilterSelections}
          onClear={() => setFilterSelections({})}
        />

        {/* Sort — shared SortControl */}
        <SortControl
          fields={RADAR_SORT_FIELDS}
          primary={sortPrimary}
          secondary={sortSecondary}
          onChange={handleSortControlChange}
          testId="radar-sort"
          defaultKey="priority"
        />

        <div style={{ flex: 1 }} />

        {/* All/Mine toggle */}
        <div data-testid="scope-toggle" style={{ display: "flex", border: "1px solid var(--v2-line)", borderRadius: 8, overflow: "hidden", fontSize: T.fsSm, fontWeight: T.fwBold }}>
          {["all", "mine"].map((s) => (
            <span
              key={s}
              data-testid={`scope-${s}`}
              onClick={() => handleSetScope(s)}
              style={{
                padding: "6px 12px", cursor: "pointer",
                background: scope === s ? "var(--v2-dark)" : "transparent",
                color: scope === s ? T.white : "var(--v2-mid)",
              }}
            >
              {s === "all" ? "All" : "Mine"}
            </span>
          ))}
        </div>

        {/* Compact density toggle */}
        <CompactSwitch
          checked={compact}
          onChange={setCompact}
          testId="radar-compact-switch"
        />

        {/* Refresh — reloads the feed from the server */}
        <button
          data-testid="radar-refresh-btn"
          onClick={loadFeed}
          style={{
            background: "var(--v2-accent)", color: T.white,
            border: "none", borderRadius: 8,
            padding: "8px 13px",
            fontSize: T.fsSm5, fontWeight: T.fwBold,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Backward-compat type-chip testid anchors — kept for existing tests; not visible.
          Clicking these still updates typeFilter (server-side API param) and filterSelections. */}
      <div data-testid="type-chips" style={{ display: "none" }}>
        <span
          data-testid="type-chip-all"
          onClick={() => { setTypeFilter(null); setFilterSelections({}); }}
        />
        {ALL_TYPES.map((t) => (
          <span
            key={t}
            data-testid={`type-chip-${t}`}
            onClick={() => {
              setTypeFilter((prev) => prev === t ? null : t);
              setFilterSelections((prev) => {
                const next = prev.type?.includes(t) ? {} : { type: [t] };
                return next;
              });
            }}
          />
        ))}
      </div>

      {/* Bands — each band is a standalone rounded card with an external pill-header row,
           matching the Leases Timeline group treatment. The single shared card shell
           is intentionally removed so no two-tone background strip appears between bands. */}
      {loading ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--v2-mid)" }}>Loading…</div>
      ) : (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 16 }}>
          {BANDS.map((band) => {
            const bandItems = itemsByBand[band.key] ?? [];
            const isEmpty = bandItems.length === 0;

            // Act now always renders (even empty — shows "You're clear" state).
            // All other empty bands are hidden entirely (no pill header, no card).
            if (isEmpty && band.key !== "act_now") return null;

            // Pill badge color by band urgency tier
            const pillColors = {
              act_now:    { bg: "var(--v2-amber-tint)", color: "var(--v2-amber)" },
              this_cycle: { bg: "var(--v2-blue-tint)",  color: "var(--v2-accent)" },
              keep_watch: { bg: "var(--v2-hair)",        color: T.mid              },
            };
            const pill = pillColors[band.key] ?? { bg: "var(--v2-hair)", color: T.mid };

            return (
              <div key={band.key}>
                {/* Pill header row — sits above (outside) the card, like Leases Timeline bands */}
                {!isEmpty && (
                  <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "2px 4px 8px" }}>
                    <span
                      data-testid={`radar-band-pill-${band.key}`}
                      style={{
                      fontSize: T.fsXs5, fontWeight: T.fwBold,
                      padding: "4px 11px", borderRadius: 8,
                      background: pill.bg, color: pill.color,
                      }}
                    >
                      {band.label}
                    </span>
                    <span style={{ fontSize: T.fsBase, color: "var(--v2-mid)" }}>{band.hint}</span>
                    <span style={{ marginLeft: "auto", fontSize: T.fsBase, fontWeight: T.fwBold, color: "var(--v2-mid)" }}>
                      {bandItems.length} items
                    </span>
                  </div>
                )}

                {/* Per-band standalone card */}
                <div
                  data-testid={`radar-band-card-${band.key}`}
                  style={{
                  background: "var(--v2-card)",
                  border: "1px solid var(--v2-line)",
                  borderRadius: 8,
                  boxShadow: "var(--v2-shadow-card)",
                  overflow: "hidden",
                  }}
                >
                  <BandCard
                    band={band}
                    items={bandItems}
                    expandedId={expandedId}
                    onToggle={handleToggleRow}
                    onResolve={handleResolve}
                    onSnooze={handleSnooze}
                    onDismiss={handleDismiss}
                    resolving={resolving}
                    snoozing={snoozing}
                    isEditor={isEditor}
                    compact={compact}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Snoozed section — editor-only */}
      {!loading && isEditor && (
        <SnoozedSection
          items={snoozedItems}
          onWakeUp={handleWakeUp}
          loading={snoozedLoading}
          error={snoozedError}
          sectionRef={snoozedSectionRef}
          open={snoozedOpen}
          onToggle={() => setSnoozedOpen((p) => !p)}
        />
      )}

      {/* Dismissed section — editor-only */}
      {!loading && isEditor && (
        <DismissedSection
          items={dismissedItems}
          onRestore={handleRestore}
          loading={dismissedLoading}
        />
      )}

      {decisionsRegisterEnabled && (
        <RadarDecisionsPanel currentRole={currentRole} />
      )}

      {/* Radar settings panel — admin-only */}
      {!loading && isAdmin && (
        <AdminRadarSettingsPanel />
      )}

      {/* Pulse animation keyframes */}
      <style>{`
        @keyframes radar-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(31,157,107,0.4); }
          70%  { box-shadow: 0 0 0 6px rgba(31,157,107,0); }
          100% { box-shadow: 0 0 0 0 rgba(31,157,107,0); }
        }
      `}</style>
      </PageHeaderStack>
    </div>
  );
}
